import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogContent, DialogTitle, IconButton, MenuItem, Select, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import { collection, onSnapshot } from 'firebase/firestore'
import { getDownloadURL, ref } from 'firebase/storage'
import { useTranslation } from 'react-i18next'
import { db, storage } from '../firebase'
import { SURVEY_BY_CODE, SURVEY_OPTIONS } from '../config/surveys'
import { exportRecordingReport } from '../utils/recordingReportExport'

const DEFAULT_TEST = 'eurobank_implicit_rt'

function dateValue(value) {
  if (typeof value?.toDate === 'function') return value.toDate()
  if (value instanceof Date) return value
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  return null
}

function formatDate(value, language) {
  const date = dateValue(value)
  return date ? new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' }).format(date) : '—'
}

function safeFilename(value) {
  return String(value || 'recording').replace(/[^a-z0-9_-]/gi, '_')
}

function videoRecordings(response) {
  const fusion = Object.entries(response.eye_tracking?.camera_recordings || {}).map(([key, recording]) => ({
    id: `camera:${key}`,
    label: `Question ${recording.question_number || '—'}${recording.step_id ? ` · ${recording.step_id}` : ''}`,
    storagePath: recording.storage_path || recording.storagePath,
    contentType: recording.content_type,
  }))
  const implicit = Object.entries(response.eye_tracking?.ad_exposures || {}).map(([advertisementId, exposure]) => {
    const recording = exposure.camera_recording || exposure.cameraRecording || {}
    return {
      id: `advertisement:${advertisementId}`,
      label: `Advertisement · ${exposure.advertisement_id || advertisementId}`,
      storagePath: recording.storage_path || recording.storagePath,
      contentType: recording.content_type,
    }
  })
  return [...fusion, ...implicit].filter((recording) => recording.storagePath)
}

function hasEyeTracking(response) {
  const tracking = response.eye_tracking || {}
  return Object.keys(tracking.recordings || {}).length > 0 || Object.keys(tracking.ad_exposures || {}).length > 0
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export default function AdminRecordingReportPage() {
  const { t, i18n } = useTranslation()
  const copy = t('adminRecordingsPage', { returnObjects: true })
  const language = String(i18n.resolvedLanguage || i18n.language || 'tr').toLowerCase().split('-')[0]
  const [surveyCode, setSurveyCode] = useState(DEFAULT_TEST)
  const [responses, setResponses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [player, setPlayer] = useState({ open: false, loading: false, url: '', title: '' })

  useEffect(() => {
    setLoading(true)
    setError('')
    const unsubscribe = onSnapshot(collection(db, 'surveys', encodeURIComponent(surveyCode), 'responses'), (snapshot) => {
      setResponses(snapshot.docs.map((item) => ({ sourceResponseId: item.id, ...item.data() })))
      setLoading(false)
    }, (loadError) => {
      console.error('Kayıt raporu yüklenemedi:', loadError)
      setError('load')
      setLoading(false)
    })
    return () => unsubscribe()
  }, [surveyCode])

  const rows = useMemo(() => responses.map((response) => ({
    response,
    videos: videoRecordings(response),
    eyeTracking: hasEyeTracking(response),
  })).filter((row) => row.videos.length || row.eyeTracking), [responses])

  const watchVideo = async (response, recording) => {
    setPlayer({ open: true, loading: true, url: '', title: `${response.bfrid || response.participant_id || response.response_id || response.sourceResponseId} · ${recording.label}` })
    setError('')
    try {
      const url = await getDownloadURL(ref(storage, recording.storagePath))
      setPlayer((current) => ({ ...current, loading: false, url }))
    } catch (watchError) {
      console.error('Video izleme bağlantısı alınamadı:', watchError)
      setPlayer((current) => ({ ...current, loading: false }))
      setError('watch')
    }
  }

  const closePlayer = () => setPlayer({ open: false, loading: false, url: '', title: '' })

  const downloadEyeTracking = (response) => {
    const exportData = {
      survey_code: surveyCode,
      response_id: response.response_id || response.sourceResponseId,
      bfrid: response.bfrid || response.participant_id || '',
      exported_at: new Date().toISOString(),
      eye_tracking: response.eye_tracking,
    }
    downloadBlob(new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' }), `${safeFilename(surveyCode)}-${safeFilename(exportData.response_id)}-eye-tracking.json`)
  }

  const downloadAllEyeTracking = () => {
    const exportData = {
      survey_code: surveyCode,
      exported_at: new Date().toISOString(),
      records: rows.filter((row) => row.eyeTracking).map(({ response }) => ({
        response_id: response.response_id || response.sourceResponseId,
        bfrid: response.bfrid || response.participant_id || '',
        eye_tracking: response.eye_tracking,
      })),
    }
    downloadBlob(new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' }), `${safeFilename(surveyCode)}-eye-tracking-records.json`)
  }

  const selectedSurvey = SURVEY_BY_CODE[surveyCode]
  const selectedSurveyName = copy.surveyOptions?.[selectedSurvey?.labelKey] || surveyCode
  const totalVideos = rows.reduce((total, row) => total + row.videos.length, 0)
  const eyeTrackingCount = rows.filter((row) => row.eyeTracking).length

  const downloadExcelReport = () => {
    try {
      exportRecordingReport({
        responses,
        surveyCode,
        surveyName: selectedSurveyName,
        recordingsForResponse: videoRecordings,
        activeLabel: copy.active,
        passiveLabel: copy.passive,
      })
    } catch (exportError) {
      console.error('Kayıt Excel raporu oluşturulamadı:', exportError)
      setError('export')
    }
  }

  return <Box sx={{ minHeight: '100vh', p: { xs: 2, md: 4 }, background: 'linear-gradient(180deg,#f7f9fc,#eef4f8)' }}>
    <Stack spacing={3} maxWidth={1400} mx="auto">
      <Box><Typography variant="h3" fontWeight={800}>{copy.title}</Typography><Typography color="text.secondary">{copy.description}</Typography></Box>
      <Card variant="outlined" sx={{ borderRadius: 3 }}><CardContent>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
          <Box><Typography variant="subtitle2" fontWeight={800}>{copy.selectTest}</Typography><Select size="small" value={surveyCode} onChange={(event) => setSurveyCode(event.target.value)} sx={{ mt: 0.75, minWidth: 300, backgroundColor: 'background.paper' }}>
            {SURVEY_OPTIONS.map((survey) => <MenuItem key={survey.code} value={survey.code}>{copy.surveyOptions?.[survey.labelKey] || survey.code}</MenuItem>)}
          </Select></Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}><Chip label={`${copy.participants}: ${responses.length}`} color="primary" variant="outlined" /><Chip label={`${copy.videos}: ${totalVideos}`} color="secondary" variant="outlined" /><Button variant="outlined" startIcon={<DownloadOutlinedIcon />} disabled={!responses.length} onClick={downloadExcelReport}>{copy.downloadExcelReport}</Button><Button variant="contained" startIcon={<DownloadOutlinedIcon />} disabled={!eyeTrackingCount} onClick={downloadAllEyeTracking}>{copy.downloadAllEyeTracking}</Button></Stack>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" mt={2}>{selectedSurvey?.country || ''} · {selectedSurvey?.segment || ''} · {copy.adminOnly}</Typography>
      </CardContent></Card>
      {error === 'load' && <Alert severity="error">{copy.loadError}</Alert>}
      {error === 'watch' && <Alert severity="error">{copy.watchError}</Alert>}
      {error === 'export' && <Alert severity="error">{copy.exportError}</Alert>}
      {loading ? <Box minHeight={250} display="grid" sx={{ placeItems: 'center' }}><CircularProgress /></Box> : !rows.length ? <Alert severity="info">{copy.empty}</Alert> : <Card variant="outlined" sx={{ borderRadius: 3 }}><CardContent><Box sx={{ overflowX: 'auto' }}><Table size="small"><TableHead><TableRow><TableCell>{copy.participant}</TableCell><TableCell>{copy.responseId}</TableCell><TableCell>{copy.reportState}</TableCell><TableCell>{copy.completedAt}</TableCell><TableCell>{copy.eyeTracking}</TableCell><TableCell>{copy.videos}</TableCell></TableRow></TableHead><TableBody>{rows.map(({ response, videos, eyeTracking }) => { const active = response.reportActive !== false; return <TableRow key={response.sourceResponseId} sx={{ opacity: active ? 1 : 0.65 }}><TableCell>{response.bfrid || response.participant_id || '—'}</TableCell><TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{response.response_id || response.sourceResponseId}</TableCell><TableCell><Chip size="small" label={active ? copy.active : copy.passive} color={active ? 'success' : 'default'} /></TableCell><TableCell>{formatDate(response.completed_at || response.updated_at, language)}</TableCell><TableCell>{eyeTracking ? <Button size="small" startIcon={<VisibilityOutlinedIcon />} onClick={() => downloadEyeTracking(response)}>{copy.downloadEyeTracking}</Button> : <Typography variant="body2" color="text.secondary">—</Typography>}</TableCell><TableCell>{videos.length ? <Stack spacing={0.5}>{videos.map((recording) => <Button key={recording.id} size="small" startIcon={<VideocamOutlinedIcon />} onClick={() => watchVideo(response, recording)}>{`${copy.watchVideo}: ${recording.label}`}</Button>)}</Stack> : <Typography variant="body2" color="text.secondary">—</Typography>}</TableCell></TableRow> })}</TableBody></Table></Box></CardContent></Card>}
    </Stack>
    <Dialog open={player.open} onClose={closePlayer} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>{player.title}<IconButton aria-label={copy.close} onClick={closePlayer} sx={{ position: 'absolute', right: 10, top: 10 }}><CloseOutlinedIcon /></IconButton></DialogTitle>
      <DialogContent>
        {player.loading ? <Box minHeight={360} display="grid" sx={{ placeItems: 'center' }}><CircularProgress /></Box> : player.url ? <Box component="video" src={player.url} controls autoPlay sx={{ display: 'block', width: '100%', maxHeight: '70vh', backgroundColor: 'black', borderRadius: 1 }} /> : <Alert severity="error">{copy.watchError}</Alert>}
      </DialogContent>
    </Dialog>
  </Box>
}
